import { useCallback, useEffect } from 'react';
import { deleteField, deleteDoc, doc, setDoc } from 'firebase/firestore';
import { getMessaging, getToken, isSupported } from 'firebase/messaging';
import { useAuth } from '../context/AuthContext';
import { app, db } from '../lib/firebase';

const TOKEN_KEY = 'kc_fcm_token';
const TOKEN_UID_KEY = 'kc_fcm_token_uid';

function readLocalTokenState() {
  return {
    token: localStorage.getItem(TOKEN_KEY) || '',
    uid: localStorage.getItem(TOKEN_UID_KEY) || '',
  };
}

function writeLocalTokenState(uid: string, token: string) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(TOKEN_UID_KEY, uid);
}

function clearLocalTokenState() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_UID_KEY);
}

/**
 * Registers/syncs web push token (FCM) for the signed-in user.
 *
 * This keeps users/{uid}.fcmToken for backward compatibility and also writes
 * users/{uid}/push_tokens/{token} for multi-device support.
 */
export function PushNotificationRegistrar() {
  const { user } = useAuth();

  const clearTokenForUser = useCallback(async (uid: string, token: string) => {
    if (!uid || !token) return;
    try {
      await setDoc(doc(db, 'users', uid), {
        fcmToken: deleteField(),
        fcmTokenUpdatedAt: deleteField(),
      }, { merge: true });
      await deleteDoc(doc(db, 'users', uid, 'push_tokens', token)).catch(() => {});
    } catch {
      // non-fatal
    }
  }, []);

  const syncPushToken = useCallback(async () => {
    const uid = user?.id;
    if (!uid) return;
    if (typeof window === 'undefined') return;
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;

    const { token: previousToken, uid: previousUid } = readLocalTokenState();

    if (Notification.permission !== 'granted') {
      if (previousToken && previousUid === uid) {
        await clearTokenForUser(uid, previousToken);
        clearLocalTokenState();
      }
      return;
    }

    const supported = await isSupported().catch(() => false);
    if (!supported) return;

    let registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      registration = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise<ServiceWorkerRegistration | null>(resolve => {
          window.setTimeout(() => resolve(null), 5000);
        }),
      ]);
    }
    if (!registration) return;

    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;
    const messaging = getMessaging(app);

    const token = await getToken(messaging, {
      serviceWorkerRegistration: registration,
      ...(vapidKey ? { vapidKey } : {}),
    }).catch(() => '');

    if (!token) return;

    // Cleanup stale token when account switches or token rotates
    if (previousToken && (previousUid !== uid || previousToken !== token)) {
      await clearTokenForUser(previousUid, previousToken);
    }

    if (previousUid === uid && previousToken === token) return;

    try {
      const nowIso = new Date().toISOString();
      await Promise.all([
        setDoc(doc(db, 'users', uid), {
          fcmToken: token,
          fcmTokenUpdatedAt: nowIso,
        }, { merge: true }),
        setDoc(doc(db, 'users', uid, 'push_tokens', token), {
          token,
          platform: 'web',
          userAgent: navigator.userAgent,
          updatedAt: nowIso,
        }, { merge: true }),
      ]);
      writeLocalTokenState(uid, token);
    } catch {
      // non-fatal
    }
  }, [clearTokenForUser, user?.id]);

  useEffect(() => {
    void syncPushToken();

    const onFocus = () => { void syncPushToken(); };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void syncPushToken();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    const interval = window.setInterval(() => { void syncPushToken(); }, 60_000);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(interval);
    };
  }, [syncPushToken]);

  return null;
}
